import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
    createUser, 
    signInUser, 
    signOutUser, 
    updateUserProfile,
    signInWithGoogle,
    signInWithFacebook,
    signInWithTwitter,
    onAuthStateChange 
  } from '../firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'

  // Check for existing session on mount
  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      console.log('Auth state changed:', user);
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      const userCredential = await signInUser(email, password);
      const user = userCredential.user;
      
      console.log('Login successful:', user);
      setUser(user); // Explicitly set user state
      setShowAuthModal(false);
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email, password, username) => {
    try {
      setLoading(true);
      
      // Check if username already exists
      const existingUsers = JSON.parse(localStorage.getItem('usernames') || '[]');
      if (existingUsers.includes(username.toLowerCase())) {
        return { success: false, error: 'Username already taken. Please choose another.' };
      }
      
      const userCredential = await createUser(email, password);
      const user = userCredential.user;
      await updateUserProfile(user, { displayName: username });
      
      // Store username to prevent duplicates
      existingUsers.push(username.toLowerCase());
      localStorage.setItem('usernames', JSON.stringify(existingUsers));
      
      console.log('Signup successful:', user);
      setUser(user); // Explicitly set user state
      setShowAuthModal(false);
      return { success: true };
    } catch (error) {
      console.error('Signup failed:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const loginWithSSO = async (provider) => {
    try {
      setLoading(true);
      let result;
      
      switch (provider) {
        case 'google':
          result = await signInWithGoogle();
          break;
        case 'facebook':
          result = await signInWithFacebook();
          break;
        case 'twitter':
          result = await signInWithTwitter();
          break;
        default:
          throw new Error(`Unsupported provider: ${provider}`);
      }
      
      console.log('SSO login successful:', result.user);
      setUser(result.user); // Explicitly set user state
      setShowAuthModal(false);
      return { success: true };
    } catch (error) {
      console.error('SSO login failed:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOutUser();
      return { success: true };
    } catch (error) {
      console.error('Logout failed:', error);
      return { success: false, error: error.message };
    }
  };

  const openAuthModal = (mode = 'login') => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  const switchAuthMode = () => {
    setAuthMode(prev => prev === 'login' ? 'signup' : 'login');
  };

  const value = {
    user,
    loading,
    showAuthModal,
    authMode,
    login,
    signup,
    loginWithSSO,
    logout,
    openAuthModal,
    closeAuthModal,
    switchAuthMode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
